import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { InsightsOverview } from "@tw-pulse/shared";
import { Button } from "@/components/ui/Button";
import { useConnectionContext } from "@/lib/connectionContext";
import { api, ApiClientError } from "@/lib/api";
import { PlugIcon, PlusIcon } from "@/lib/icons";

type Loadable<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

export function DashboardPage() {
  const ctx = useConnectionContext();

  // Pass connection IDs only in subset mode. "All" = no $in clause server-side.
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [overview, setOverview] = useState<Loadable<InsightsOverview>>({
    status: "loading",
  });

  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setOverview({ status: "loading" });

    void api.insights
      .overview(scope)
      .then((data) => {
        if (!cancelled) setOverview({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setOverview({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Overview
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Operations dashboard
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            {scopeLabel(ctx)} · Last 24 hours.
          </p>
        </div>
        <Link to="/connections/new">
          <Button>
            <PlusIcon size={14} />
            Connect Twilio
          </Button>
        </Link>
      </header>

      {ctx.status === "ready" && ctx.connections.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <StatGrid overview={overview} />
          <SecondaryGrid overview={overview} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatGrid({ overview }: { overview: Loadable<InsightsOverview> }) {
  if (overview.status === "loading") {
    return <SkeletonGrid count={4} />;
  }
  if (overview.status === "error") {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
        {overview.message}
      </p>
    );
  }
  const o = overview.data;
  const cards: Array<{ label: string; value: string; hint: string }> = [
    {
      label: "Messages · 24h",
      value: formatInt(o.messages.total),
      hint:
        o.messages.delivered + o.messages.failed > 0
          ? `${formatInt(o.messages.delivered)} delivered · ${formatInt(o.messages.failed)} failed`
          : "Awaiting traffic",
    },
    {
      label: "Delivery rate · 24h",
      value:
        o.messages.deliveryRate === null ? "—" : `${(o.messages.deliveryRate * 100).toFixed(1)}%`,
      hint:
        o.messages.deliveryRate === null
          ? "0 messages reached terminal state"
          : `${formatInt(o.messages.delivered)} of ${formatInt(o.messages.delivered + o.messages.failed)}`,
    },
    {
      label: "Spend · 24h",
      value: formatMoney(o.messages.spend.amount + o.calls.spend.amount),
      hint: o.messages.spend.currency
        ? `${o.messages.spend.currency.toUpperCase()} · msgs + calls`
        : "USD · msgs + calls",
    },
    {
      label: "Active senders · 24h",
      value: formatInt(o.messages.activeSenders),
      hint: `Across ${formatInt(o.phoneNumbers.total)} phone numbers`,
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((s) => (
        <Card key={s.label} {...s} />
      ))}
    </div>
  );
}

function SecondaryGrid({ overview }: { overview: Loadable<InsightsOverview> }) {
  if (overview.status === "loading") return <SkeletonGrid count={3} />;
  if (overview.status === "error") return null;
  const o = overview.data;
  const cards: Array<{ label: string; value: string; hint: string }> = [
    {
      label: "Calls · 24h",
      value: formatInt(o.calls.total),
      hint:
        o.calls.completed + o.calls.failed > 0
          ? `${formatInt(o.calls.completed)} completed · ${formatInt(o.calls.failed)} unanswered/failed`
          : "Awaiting traffic",
    },
    {
      label: "Avg call duration · 24h",
      value: o.calls.avgDurationSec === null ? "—" : formatDuration(o.calls.avgDurationSec),
      hint:
        o.calls.completed > 0
          ? `Across ${formatInt(o.calls.completed)} completed calls`
          : "0 completed calls",
    },
    {
      label: "Errors · 24h",
      value: formatInt(o.errors24h),
      hint: o.errors24h === 0 ? "All clear" : "Twilio Alerts",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {cards.map((s) => (
        <Card key={s.label} {...s} />
      ))}
    </div>
  );
}

function Card({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-card-light transition hover:border-zinc-300 dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02] dark:hover:border-zinc-700/80 dark:hover:bg-zinc-900/60">
      <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
        {label}
      </div>
      <div className="mt-1.5 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
        {value}
      </div>
      <div className="mt-1 text-[11.5px] text-zinc-500 dark:text-zinc-500">{hint}</div>
    </div>
  );
}

function SkeletonGrid({ count }: { count: number }) {
  return (
    <div
      className={
        count === 4
          ? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
          : "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      }
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-zinc-200 bg-white p-4 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="mt-3 h-7 w-16 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          <div className="mt-2 h-3 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white p-10 text-center shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-brand-500/10 to-transparent" />
      <div className="relative mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-brand-500/15 text-brand-600 ring-1 ring-inset ring-brand-500/30 dark:text-brand-300">
        <PlugIcon size={22} />
      </div>
      <h2 className="relative text-base font-semibold text-zinc-900 dark:text-zinc-50">
        Connect a Twilio account to bring your data to life
      </h2>
      <p className="relative mx-auto mt-1.5 max-w-md text-[13.5px] text-zinc-600 dark:text-zinc-400">
        We start ingesting telemetry within seconds of connection. Health, cost, deliverability, and
        engagement insights surface as soon as data flows.
      </p>
      <div className="relative mt-5">
        <Link to="/connections/new">
          <Button>
            <PlusIcon size={14} />
            Connect Twilio
          </Button>
        </Link>
      </div>
    </div>
  );
}

function scopeLabel(ctx: ReturnType<typeof useConnectionContext>): string {
  if (ctx.connections.length === 0) return "No accounts connected yet";
  if (ctx.allSelected)
    return `Across all ${ctx.connections.length} ${ctx.connections.length === 1 ? "account" : "accounts"}`;
  if (ctx.selectedIds.length === 1) {
    const c = ctx.connections.find((x) => x.id === ctx.selectedIds[0]);
    return `Scoped to ${c?.friendlyName ?? c?.accountSid ?? "1 account"}`;
  }
  return `Scoped to ${ctx.selectedIds.length} of ${ctx.connections.length} accounts`;
}

function formatInt(n: number): string {
  return n.toLocaleString("en-US");
}

function formatMoney(n: number): string {
  if (n === 0) return "$0.00";
  return `$${n.toFixed(n < 1 ? 4 : 2)}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
